import React, { useCallback, useContext, useState } from "react";
import { Activity, Bike, Cpu, Heart, LayoutGrid, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useAppState, WorkoutContext } from "../../contexts/WorkoutContext";
import api from "../../services/api";
import useAudioSettings from "../../hooks/useAudioSettings";
import useTodaySuggestion from "../../hooks/useTodaySuggestion";
import { SOUND_PROFILES } from "../../config/soundProfiles";
import { requestStartToday } from "../../utils/setupFlow";
import { cn } from "../../lib/utils";
import TodayCard from "../session/TodayCard";
import { Group, ItemRow } from "../profile/ProfileParts";
import BluetoothSheet, { equipmentIcon, useBluetoothItems, useLiveLine } from "../settings/BluetoothSheet";
import SoundsSheet from "../settings/SoundsSheet";
import TimerSheet from "../settings/TimerSheet";
import useKhronosTimers from "../settings/useKhronosTimers";
import { formatLastSeen } from "../settings/settingsSummaries";
import TimersList, { useTimerLauncher } from "./TimersList";

/** One gear tile: green when ready, dashed when not; tapping fixes it. */
const GearTile = ({ Icon, title, status, ready, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "grid min-w-0 grid-cols-[1.125rem_minmax(0,1fr)] items-center gap-x-2 gap-y-0.5 rounded-xl border bg-card px-2.5 py-2 text-left transition-colors hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-card",
      ready ? "border-border" : "border-dashed border-border",
    )}
  >
    <Icon className={cn("row-span-2 h-[18px] w-[18px]", ready ? "text-green-500" : "text-muted-foreground/70")} />
    <span className="truncate text-[13px] font-semibold">{title}</span>
    <span className={cn("truncate text-xs", ready ? "text-green-500" : "text-muted-foreground")}>{status}</span>
  </button>
);

const HeartTile = ({ eq, items, onOpen }) => {
  const hr = items.find((i) => i.key === "hr") || null;
  const line = useLiveLine(hr);
  if (!eq.isSupported) {
    return <GearTile Icon={Heart} title="Heart rate" status="Needs Chrome or Edge" disabled />;
  }
  if (hr) {
    const connected = hr.status === "connected";
    return (
      <GearTile
        Icon={Heart}
        title={hr.name || "Heart rate"}
        status={connected ? line || "Connected" : hr.status === "reconnecting" ? "Reconnecting…" : "Not connected"}
        ready={connected}
        onClick={() => onOpen("hr")}
      />
    );
  }
  const saved = items.find((i) => i.key === "saved-hr");
  if (saved) {
    return <GearTile Icon={Heart} title={saved.name || "Heart rate"} status="Saved · tap to pair" onClick={() => onOpen("saved-hr")} />;
  }
  return (
    <GearTile
      Icon={Heart}
      title="Heart rate"
      status={eq.hrPairing ? "Pairing…" : "Tap to pair a strap"}
      onClick={() => !eq.hrPairing && eq.addHrMonitor()}
    />
  );
};

const MachineTile = ({ eq, items, onOpen }) => {
  const machines = items.filter((i) => i.type !== "hr_monitor");
  const machine = machines.find((i) => i.active) || machines.find((i) => i.kind === "ftms") || machines[0] || null;
  const line = useLiveLine(machine?.kind === "ftms" ? machine : null);
  if (!eq.isSupported) {
    return <GearTile Icon={Bike} title="Bike or rower" status="Needs Chrome or Edge" disabled />;
  }
  if (!machine) {
    return (
      <GearTile
        Icon={Bike}
        title="Bike or rower"
        status={eq.pairing ? "Pairing…" : "Not paired"}
        onClick={() => !eq.pairing && eq.addDevice()}
      />
    );
  }
  const connected = machine.status === "connected";
  return (
    <GearTile
      Icon={equipmentIcon(machine.type)}
      title={machine.name || "Bike or rower"}
      status={machine.kind === "saved" ? "Saved · tap to pair" : connected ? line || "Connected" : "Not connected"}
      ready={connected}
      onClick={() => onOpen(machine.key)}
    />
  );
};

/**
 * The Coach tab with nothing loaded (#109): the Coach at rest rather than a
 * second menu. A ready check for your gear, a still ring, today's suggestion
 * (the same one the Session tab shows), and the timers.
 */
const CoachAtRest = () => {
  const { isAuthenticated } = useAuth();
  const { setView } = useContext(WorkoutContext);
  const { setSettingsSheet } = useAppState();
  const today = useTodaySuggestion();
  const launcher = useTimerLauncher();

  const syncToApi = useCallback(
    (fields) => {
      if (isAuthenticated) api.updateSettings(fields).catch(console.error);
    },
    [isAuthenticated],
  );
  const [audio, updateAudio] = useAudioSettings({ onSync: syncToApi });
  const { eq, items } = useBluetoothItems();
  const timers = useKhronosTimers(isAuthenticated);

  // Which gear sheet is open: "sounds", { timer } or { ble }.
  const [sheet, setSheet] = useState(null);
  const close = () => setSheet(null);

  const openSettingsSheet = (kind) => {
    setSettingsSheet?.(kind);
    setView("settings");
  };

  // Start runs a programme session that's already built right here; anything
  // that still needs generating goes through the Session tab, which owns the
  // generate screen. The shape shown here goes with it, so it builds exactly
  // this one; a programme session still to build is left to the Session tab.
  const start = () => {
    if (today.programmeSlot?.plan_data) {
      today.startProgrammeSlot(today.programmeSlot.plan_data);
      return;
    }
    requestStartToday("start", today.programmeSlot ? null : today.suggestionOverrides());
    setView("session");
  };
  const customise = () => {
    requestStartToday("customise", today.suggestionOverrides());
    setView("session");
  };

  const timer =
    timers.devices.find((d) => timers.online[d.id]) || timers.devices[0] || null;
  const timerOnline = timer ? timers.online[timer.id] === true : false;
  const soundSet = SOUND_PROFILES[audio.profile]?.label || audio.profile;

  return (
    <div className="mx-auto grid w-full max-w-5xl grid-cols-[minmax(0,1fr)] gap-6 pb-8 lg:grid-cols-2 lg:gap-x-8">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        <section aria-label="Ready check" className="grid grid-cols-2 gap-1.5">
          <HeartTile eq={eq} items={items} onOpen={(key) => setSheet({ ble: key })} />
          <MachineTile eq={eq} items={items} onOpen={(key) => setSheet({ ble: key })} />
          {!isAuthenticated ? (
            <GearTile Icon={Cpu} title="Khronos Timer" status="Sign in to link" onClick={() => setView("settings")} />
          ) : timer ? (
            <GearTile
              Icon={Cpu}
              title={timer.name}
              status={timerOnline ? "Online" : `Offline · seen ${formatLastSeen(timer.last_seen_at).toLowerCase()}`}
              ready={timerOnline}
              onClick={() => setSheet({ timer: timer.id })}
            />
          ) : (
            <GearTile
              Icon={Cpu}
              title="Khronos Timer"
              status={timers.loading ? "Checking…" : "Not linked"}
              onClick={() => openSettingsSheet("link")}
            />
          )}
          {audio.muted ? (
            <GearTile Icon={VolumeX} title="Sound" status="Muted · tap to unmute" onClick={() => updateAudio({ muted: false })} />
          ) : (
            <GearTile
              Icon={Volume2}
              title="Sound"
              status={`${soundSet} · ${Math.round(audio.volume * 100)}%`}
              ready
              onClick={() => setSheet("sounds")}
            />
          )}
        </section>

        <div className="grid justify-items-center gap-2 py-1" aria-hidden="true">
          <div className="relative grid h-36 w-36 place-items-center rounded-full border-[10px] border-muted/40">
            <div className="absolute inset-2 rounded-full border border-dashed border-muted/60" />
            <span className="font-mono text-3xl font-semibold tabular-nums text-muted-foreground/50">--:--</span>
          </div>
          <p className="text-sm text-muted-foreground">Nothing loaded yet</p>
        </div>

        {today.programmeLoading && isAuthenticated ? (
          <div aria-hidden="true" className="h-[13.5rem] animate-pulse rounded-2xl border border-border bg-muted/30" />
        ) : (
          <TodayCard
            programme={today.programme}
            programmeSlot={today.programmeSlot}
            selector={today.selector}
            preview={today.preview}
            sessionMinutes={today.sessionMinutes}
            isNewUser={today.isNewUser}
            onStart={start}
            onShuffle={today.shuffle}
            onCustomise={customise}
            onOpenProgramme={() => setView("programme")}
          />
        )}
      </div>

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        <TimersList launcher={launcher} />
        <Group title="More">
          {/* Free training left Quick timers (#163): it's a way to train. */}
          <ItemRow
            Icon={Activity}
            title="Just train"
            detail="Free training: your plan or none. Log what you do as you go"
            onClick={() => launcher.openQuick("free")}
          />
          <ItemRow
            Icon={LayoutGrid}
            title="More ways to start"
            detail="Build your own, saved sessions, recent ones"
            onClick={() => setView("session")}
          />
        </Group>
      </div>

      {launcher.sheet}
      <SoundsSheet
        open={sheet === "sounds"}
        onClose={close}
        audio={audio}
        updateAudio={updateAudio}
        hasTimer={timers.devices.length > 0}
      />
      <TimerSheet deviceId={sheet?.timer ?? null} timers={timers} onClose={close} />
      <BluetoothSheet itemKey={sheet?.ble ?? null} onClose={close} />
    </div>
  );
};

export default CoachAtRest;
